import { PARAMETER_VALUE_TYPE } from '@/constants/ParameterValueTypes.js';

const asArray = value => (Array.isArray(value) ? value : []);

export const createEmptySubset = () => ({
    type: '',
    subset: [],
    value: '',
    isActive: true,
    isSignificant: false,
    deactivateOthers: false,
    subsets: [],
});

export const toSubsetIdArray = value => {
    if (value == null || value === '') {
        return [];
    }

    return asArray(value)
        .map(item => {
            if (item == null || item === '') {
                return null;
            }
            if (typeof item === 'object') {
                return item.id ?? item.code ?? item.name ?? null;
            }
            return item;
        })
        .filter(item => item != null && item !== '');
};

const getRawSubsetSelection = node => node?.subset ?? node?.targets ?? node?.subsetIds ?? node?.values ?? node?.filters ?? node?.ids;

const isApiSubset = node => Array.isArray(node?.branches);

const flattenApiSubset = node => {
    const deactivateOthers = node?.restBranch?.isActive === false;

    return asArray(node?.branches).map(branch => ({
        type: node?.type ?? '',
        subset: toSubsetIdArray(branch?.targets),
        value: branch?.value ?? '',
        isActive: branch?.isActive !== false,
        isSignificant: Boolean(branch?.isSignificant),
        deactivateOthers,
        subsets: normalizeParameterSubsets(branch?.subsets),
    }));
};

export const normalizeParameterSubsets = nodes =>
    asArray(nodes).flatMap(node => {
        if (isApiSubset(node)) {
            return flattenApiSubset(node);
        }

        return [
            {
                type: node?.type ?? node?.subsetType ?? node?.filterType ?? '',
                subset: toSubsetIdArray(getRawSubsetSelection(node)),
                value: node?.value ?? '',
                isActive: node?.isActive !== false,
                isSignificant: Boolean(node?.isSignificant),
                deactivateOthers: Boolean(node?.deactivateOthers ?? node?.excludeOthers ?? node?.restBranch?.isActive === false),
                subsets: normalizeParameterSubsets(node?.subsets ?? node?.children ?? node?.exceptions),
            },
        ];
    });

export const normalizeSubsetValue = (value, type) => {
    let normalizedValue = value == null ? '' : String(value).trim();

    if (!normalizedValue) {
        return null;
    }

    if (type === PARAMETER_VALUE_TYPE.FLOAT) {
        normalizedValue = normalizedValue.replace(',', '.');
    } else if (type === PARAMETER_VALUE_TYPE.BOOLEAN) {
        normalizedValue = value === true || normalizedValue === 'true' ? 'true' : 'false';
    }

    return normalizedValue;
};

const groupUiSubsetsByType = nodes => {
    const order = [];
    const groupsByType = new Map();

    asArray(nodes)
        .filter(node => node?.type)
        .forEach(node => {
            if (!groupsByType.has(node.type)) {
                groupsByType.set(node.type, []);
                order.push(node.type);
            }
            groupsByType.get(node.type).push(node);
        });

    return { order, groupsByType };
};

export const normalizeParameterRecord = record => ({
    ...record,
    subsets: normalizeParameterSubsets(record?.subsets ?? record?.exceptions),
});

export const serializeParameterSubsets = (nodes, parameterType) => {
    const { order, groupsByType } = groupUiSubsetsByType(nodes);

    return order.map(type => {
        const groups = groupsByType.get(type);
        const deactivateOthers = groups.some(group => group.deactivateOthers);

        return {
            type,
            branches: groups.map(group => ({
                value: normalizeSubsetValue(group.value, parameterType),
                targets: toSubsetIdArray(group.subset),
                isActive: group.isActive !== false,
                isSignificant: Boolean(group.isSignificant),
                subsets: serializeParameterSubsets(group.subsets, parameterType),
            })),
            restBranch: {
                isActive: !deactivateOthers,
                subsets: [],
            },
        };
    });
};

export const resetSubsetValuesDeep = (nodes, path, setValue) => {
    asArray(nodes).forEach((node, index) => {
        const nodePath = `${path}.${index}`;
        setValue(`${nodePath}.value`, '', { shouldDirty: true });
        resetSubsetValuesDeep(node?.subsets, `${nodePath}.subsets`, setValue);
    });
};

export const validateRequiredSubset = (value, translate) => {
    if (!Array.isArray(value) || value.length === 0) {
        return translate('common.validation.required');
    }

    return undefined;
};
